"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useSettingsStore } from "@/lib/settings-store"
import { getImageUrl } from "@/lib/image-utils"
import {
  FLIP_INTERVAL_MS,
  HOVER_DWELL_MS,
  fetchEromeFrames,
  framesFromPost,
  getCachedEromeFrames,
  canFetchEromeFrames,
  type AlbumPostLike,
} from "@/lib/album-preview"

/**
 * Hover flip-through for album cards (see lib/album-preview.ts). Off unless the "Album hover preview" setting is on.
 * `start()` on mouse-enter, `stop()` on mouse-leave. After the pointer has rested on the card for a moment it steps through the
 * album's pictures; the visible frame is the only state, and it changes about once a second, so a card re-renders rarely.
 * Touch screens have no hover, so nothing runs there (the force-preview setting covers phones for videos).
 */
export function useAlbumHoverPreview(post: AlbumPostLike) {
  const enabled = useSettingsStore((state) => state.albumHoverPreview)
  const [frame, setFrame] = useState<{ src: string; n: number; total: number } | null>(null)
  const run = useRef(0)
  const dwell = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tick = useRef<ReturnType<typeof setInterval> | null>(null)

  const clear = useCallback(() => {
    run.current += 1
    if (dwell.current) clearTimeout(dwell.current)
    if (tick.current) clearInterval(tick.current)
    dwell.current = null
    tick.current = null
    setFrame(null)
  }, [])

  const start = useCallback(() => {
    if (!enabled) return
    if (typeof window !== "undefined" && !window.matchMedia("(hover: hover)").matches) return
    const mine = ++run.current
    if (dwell.current) clearTimeout(dwell.current)
    dwell.current = setTimeout(async () => {
      let urls = framesFromPost(post)
      if (urls.length < 2 && canFetchEromeFrames(post) && post.source) {
        urls = getCachedEromeFrames(post.source) ?? (await fetchEromeFrames(post.source))
      }
      if (run.current !== mine || urls.length < 2) return // moved away meanwhile, or nothing to flip through
      const srcs = urls.map((url) => getImageUrl(url, { site: post.site ?? undefined }))
      let i = 0
      setFrame({ src: srcs[0], n: 1, total: srcs.length })
      tick.current = setInterval(() => {
        i = (i + 1) % srcs.length
        const next = new Image() // warm the one after this
        next.src = srcs[(i + 1) % srcs.length]
        setFrame({ src: srcs[i], n: i + 1, total: srcs.length })
      }, FLIP_INTERVAL_MS)
    }, HOVER_DWELL_MS)
  }, [enabled, post])

  useEffect(() => clear, [clear])
  useEffect(() => {
    if (!enabled) clear()
  }, [enabled, clear])

  return { frame, start, stop: clear }
}
